import { computed, type ComputedRef } from 'vue'
import { useCore } from '@/composables/core'
import { getExternalId } from '@/lib/analytics/helpers'
import { useOnboardingStore } from '@/stores/onboarding'
import { USER_TYPE_BUSINESS } from './consts'

type AnalyticsBusiness = {
  id?: string | number
  primary_category_internal_name?: string
}

type AnalyticsCommonParamsRootState = {
  routingConfig: { config: unknown }
  app: { country: string }
  identity?: { user?: { id?: string | number } }
  business?: { business?: AnalyticsBusiness }
}

export type UseAnalyticsCommonParamsReturn = {
  businessId: ComputedRef<string | null>
  country: ComputedRef<string>
  userId: ComputedRef<string | null>
  userType: ComputedRef<string>
  businessCategory: ComputedRef<string | null>
}

export function useAnalyticsCommonParams (): UseAnalyticsCommonParamsReturn {
  const { store } = useCore()
  const onboardingStore = useOnboardingStore()

  const routingConfig = computed(() => (store.state as AnalyticsCommonParamsRootState).routingConfig.config)
  const country = computed(() => (store.state as AnalyticsCommonParamsRootState).app.country)
  const user = computed(() => (store.state as AnalyticsCommonParamsRootState).identity?.user)
  const business = computed(() => (store.state as AnalyticsCommonParamsRootState).business?.business)

  const _getExternalId = (id: string | number | undefined): string | null => {
    return getExternalId({
      id,
      country: country.value,
      routingConfig: routingConfig.value,
    })
  }

  const userId = computed(() => _getExternalId(user.value?.id))
  const businessId = computed(() => _getExternalId(business.value?.id))
  const userType = computed(() => USER_TYPE_BUSINESS)
  const businessCategory = computed<string | null>(() => {
    const onboardingBusiness = onboardingStore.business as AnalyticsBusiness
    return business.value?.primary_category_internal_name
      ?? onboardingBusiness?.primary_category_internal_name
      ?? null
  })

  return {
    businessId,
    country,
    userId,
    userType,
    businessCategory,
  }
}
